/* EcologyIntroCard — 2-second arena intro card that appears at the
 * start of every battle. Shows the ecological arena name plus one
 * fact about the shared habitat, then fades out so play can begin.
 *
 * The card is INTENTIONALLY light — no photos, no scrollable content —
 * because it should feel like a natural-history subtitle, not a
 * cutscene. It respects `reducedMotion` and auto-dismisses on tap.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf } from 'lucide-react';

const DEFAULT_DURATION_MS = 2200;

// Arena → one-sentence ecology hook. Keeps the intro readable without
// asking the backend for a per-battle fact (that would slow the fight).
const ARENA_FACTS = {
  forest:    'Canopy cover gives ground creatures the drop on aerial hunters.',
  wetland:   'Amphibians and freshwater fish thrive where water meets vegetation.',
  swamp:     'Slow water and dense cover reward stealth and patient hunters.',
  mountain:  'Thermal currents lift birds while thin air punishes slow movers.',
  alpine:    'Thermal currents lift birds while thin air punishes slow movers.',
  grassland: 'Open range favours running mammals and pollinating insects.',
  prairie:   'Open range favours running mammals and pollinating insects.',
  tundra:    'Insulated mammals and cold-tolerant birds dominate the cold.',
  ocean:     'Home waters give fish a decisive edge over land-bound rivals.',
  desert:    'Heat-tolerant reptiles and insects surge in native heat.',
};

function factFor(arenaKey) {
  const k = String(arenaKey || '').toLowerCase();
  return ARENA_FACTS[k] || 'Every arena is chosen from both species\' real habitat associations.';
}

export default function EcologyIntroCard({
  arenaLabel,
  arenaKey,
  isSharedHabitat,
  reducedMotion = false,
  durationMs = DEFAULT_DURATION_MS,
  onDismiss,
}) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setVisible(false), durationMs);
    return () => clearTimeout(t);
  }, [durationMs]);
  useEffect(() => {
    if (!visible) {
      const t = setTimeout(() => onDismiss?.(), 400);
      return () => clearTimeout(t);
    }
  }, [visible, onDismiss]);

  const label = String(arenaLabel || arenaKey || 'Arena').toUpperCase();
  const fact = factFor(arenaKey);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          onClick={() => setVisible(false)}
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: reducedMotion ? 0.1 : 0.45 }}
          data-testid="ecology-intro-card"
          aria-label={`${label}. ${fact}. Tap to dismiss.`}
          className="absolute inset-x-0 top-24 mx-auto z-40 max-w-md px-4 pointer-events-auto"
        >
          <div className="rounded-2xl bg-black/75 border border-emerald-400/50 shadow-[0_0_24px_-4px_rgba(52,211,153,0.55)] px-5 py-3 text-left backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-1">
              <Leaf className="w-3.5 h-3.5 text-emerald-300" strokeWidth={2.4} />
              <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">
                {isSharedHabitat ? 'Shared habitat' : 'Ecological arena'}
              </span>
            </div>
            <p className="font-serif text-lg leading-tight text-white" data-testid="ecology-intro-label">
              {label}
            </p>
            <p className="text-[11px] leading-snug text-emerald-100/85 mt-1" data-testid="ecology-intro-fact">
              {fact}
            </p>
          </div>
        </motion.button>
      )}
    </AnimatePresence>
  );
}
